// The map as render.ts sees it (RenderMap), on top of a MapLibre map.
//
// Zooms. Leaflet and MapLibre use the same Web Mercator world, but Leaflet's
// world is 256 px wide at zoom 0 and MapLibre's is 512 px (512 px tiles). So
// MapLibre zoom z shows exactly what Leaflet showed at z + 1. The renderer's
// sizes (trainScale, railGap, SPLIT_Z...) were tuned in Leaflet zooms, so it
// is fed MapLibre's zoom + ZOOM_OFFSET, and ?z= on the page takes that scale.
//
// Pixels. The renderer places trains at world pixel (256 * 2^z px world) minus
// getOrigin(), the world pixel of the container's top-left corner. That's
// exact for a north-up, unpitched Mercator map, which app.ts enforces
// (rotation and pitch off).

import type { Map as MapLibreMap } from 'maplibre-gl';
import type { RenderMap } from './render.ts';
import type { ScreenPoint } from './types.ts';

// Leaflet zoom = MapLibre zoom + ZOOM_OFFSET.
export const ZOOM_OFFSET = 1;
const TILE = 256;
// Web Mercator's latitude limit.
const MAX_LAT = 85.0511287798;

// A latitude and longitude's world pixel at Leaflet zoom 0 (a 256 px world).
// Plain math, so the renderer can project every track point without calling
// into MapLibre.
export function project0(lat: number, lng: number): ScreenPoint {
  const phi = (Math.max(-MAX_LAT, Math.min(MAX_LAT, lat)) * Math.PI) / 180;
  return {
    x: ((lng + 180) / 360) * TILE,
    y: (0.5 - Math.log(Math.tan(Math.PI / 4 + phi / 2)) / (2 * Math.PI)) * TILE,
  };
}

export interface MapView extends RenderMap {
  // Read the camera: call before each draw. The renderer asks for the zoom
  // and origin several times per draw; they're computed once here.
  readonly sync: () => void;
}

export function mapView(map: MapLibreMap): MapView {
  const container = map.getContainer();
  let zoom = map.getZoom() + ZOOM_OFFSET;
  let origin: ScreenPoint = { x: 0, y: 0 };
  let w = container.clientWidth;
  let h = container.clientHeight;

  function sync(): void {
    zoom = map.getZoom() + ZOOM_OFFSET;
    w = container.clientWidth;
    h = container.clientHeight;
    // Anchor on the center through MapLibre's own projection, so the origin
    // stays right whatever camera padding or offset MapLibre applies.
    const c = map.getCenter();
    const world = project0(c.lat, c.lng);
    const scale = 2 ** zoom;
    const at = map.project(c);
    origin = { x: world.x * scale - at.x, y: world.y * scale - at.y };
  }
  sync();

  return {
    sync,
    getZoom: () => zoom,
    getSize: () => ({ x: w, y: h }),
    getOrigin: () => origin,
    project: project0,
    resize: () => {
      map.resize();
      sync();
    },
  };
}
